import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ToastStatus } from '../../constants/toastMessage.constants';

import {
    FormulaDetailModel,
    FormulaUpdateBase,
} from '../../models/formula.model';

import API from '../../services/api';
import { toastService } from '../../services/toast.service';

import customEmailTemplatesKeys from '../custom-email-templates/_querykeys';
import formulaKeys from './_queryKeys';

interface BulkRemapParams {
    messages: FormulaDetailModel[];
    emailTemplateFieldMappings: Record<string, string | null>;
    emailTemplateId: string;
    isAampeCustomTemplate: boolean;
    templateId?: string;
}

const useFormulaBulkRemapTemplate = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            messages,
            emailTemplateFieldMappings,
            emailTemplateId,
            isAampeCustomTemplate,
        }: BulkRemapParams) => {
            return Promise.all(
                messages.map((message) => {
                    const payload: FormulaUpdateBase = {
                        id: message.id,
                        email_template_field_mappings:
                            emailTemplateFieldMappings,
                        additional_metadata: {
                            ...(message.additional_metadata ?? {}),
                            email_template_id: emailTemplateId,
                            is_aampe_custom_template: isAampeCustomTemplate,
                        },
                    };
                    return API.updateFormula(payload);
                }),
            );
        },

        onSuccess: (_data, variables) => {
            const count = variables.messages.length;
            toastService.showNotificationToast(
                ToastStatus.Success,
                count === 1
                    ? 'Email template has been mapped and updated.'
                    : `Email template has been mapped and updated for ${count} message groups.`,
            );

            queryClient.invalidateQueries({ queryKey: formulaKeys.all });
            queryClient.invalidateQueries({
                queryKey: formulaKeys.formulaFetchAll(),
            });

            if (variables.templateId) {
                queryClient.invalidateQueries({
                    queryKey:
                        customEmailTemplatesKeys.customEmailTemplatesMessages(
                            variables.templateId,
                        ),
                });
                queryClient.invalidateQueries({
                    queryKey: customEmailTemplatesKeys.detail(
                        variables.templateId,
                    ),
                });
            }
        },

        onError: (error: Error) => {
            toastService.showNotificationToast(
                ToastStatus.Error,
                `Failed to remap template: ${error.message}`,
            );
        },
    });
};

export default useFormulaBulkRemapTemplate;
